/**
 * @file grn-details.tsx
 * @deprecated Copied to app/modules/procurement/pages/goods-received-note/grn-details.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/goods-received-note/grn-details
 * @description Goods Received Note detail view showing full GRN information, linked PO
 *   details, received quantities per variant, quality checks, batch numbers, and expiry
 *   dates. Provides workflow actions (accept, reject, partial receive) with modal dialogs
 *   for batch tracking and inventory addition.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo [P1] Implement fetchGRNDetails() error handling:
 *   - Wrap API call in try/catch
 *   - Show error toast if GRN ID not found
 *   - Redirect to list on 404 error
 *   - Reference: purchase-order-details-v2.tsx error pattern
 *
 * @todo [P1] Implement GRN status workflow actions:
 *   - handleAccept() - POST accept action, update inventory, mark PO as completed
 *   - handlePartialReceive() - POST action for partial quantities
 *   - handleReject() - POST reject action, keep PO in pending state
 *   - Show confirmation modal before state transitions
 *   - Reference: add-goods-to-inventory.tsx action handlers
 *
 * @todo [P2] Add batch & expiry tracking UI:
 *   - Display batch numbers and expiry dates for perishable goods
 *   - Show warnings for items expiring soon (< 30 days)
 *   - Link to batch details/expiry alerts in inventory
 *   - Support editing batch info if GRN still pending
 *
 * @todo [P2] Implement linked PO navigation:
 *   - Display full PO details inline (supplier, variants, quantities)
 *   - Add link to navigate to full PO details
 *   - Show PO status and expected vs received quantities
 *   - Reference: purchase-order-details-v2.tsx shipment linking
 *
 * @todo [P2] Add quality check workflow:
 *   - Display quality inspection results if available
 *   - Record damage/defect notes during GRN acceptance
 *   - Link to quality check checklist or damage report
 *   - Support image/attachment uploads for damaged items
 *
 * @todo [P2] Add audit logging for GRN actions:
 *   - Log who accepted/rejected GRN and when
 *   - Include acceptance/rejection notes in audit trail
 *   - Sync with _auditLog platform on backend
 *   - Reference: add-purchase-order.tsx audit logging pattern
 *
 * @todo [P3] Add GRN status history timeline:
 *   - Display full lifecycle: created → received → accepted/rejected
 *   - Show who created GRN and who accepted it
 *   - Display timestamp for each status change
 *   - Reference: escalation details comment timeline pattern
 */

import { Button, Card, Col, Row, Form, InputGroup } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../../Global/loader";
import {
  Admin,
  AllModules,
  CartonWithDozens,
  CartonWithPieces,
  Dozen,
  PermissionModule,
  Permissions,
  Piece,
} from "../../../../utils/constants";
import clsx from "clsx";
import UploadImg from "../../../../_admin/assets/media/upload.png";
import Method from "../../../../utils/methods";
import APICallService from "../../../../api/apiCallService";
import { poManagementEndPoints, inventory } from "../../../../api/apiEndPoints";
import { error, success } from "../../../../Global/toast";
import { CustomSelect } from "../../../custom/Select/CustomSelect";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../../custom/DateRange/dateRange.css";
import BatchIcon from "../../../../_admin/assets/media/product/batch.png";
import Remove from "../../../../_admin/assets/media/product/remove.png";
import { useWarehouse } from "../../../context/WarehouseContext";
import { useAuth } from "../../auth";
import ImageWithFallback from "../../_common/components/image-with-fallback";

// East Africa Time (EAT, UTC+3) Timezone Helpers
const convertToEATDate = (d: Date | string | null): Date => {
  if (!d) return new Date();
  const dateObj = typeof d === "string" ? new Date(d) : d;
  const offsetDiffMinutes = 180 + dateObj.getTimezoneOffset(); // EAT is +3 hours (180 mins)
  return new Date(dateObj.getTime() + offsetDiffMinutes * 60 * 1000);
};

const convertEATLocalDateToRealDate = (
  localEATDate: Date | null,
): Date | null => {
  if (!localEATDate) return null;
  const offsetDiffMinutes = 180 + localEATDate.getTimezoneOffset();
  return new Date(localEATDate.getTime() - offsetDiffMinutes * 60 * 1000);
};

const markPurchaseOrderOptions = [
  { value: "1", label: "Partially Received" },
  { value: "2", label: "Completed" },
  { value: "3", label: "Rejected" },
];

const quantityDiscrepancyOptions = [
  { value: "1", label: "Missing item" },
  { value: "2", label: "Damaged item" },
  { value: "3", label: "Partial delivery" },
];

const SpinnerButton = ({
  onClick,
  direction,
}: {
  onClick: () => void;
  direction: "up" | "down";
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="uw-btn uw-btn-icon uw-btn-sm btn btn-sm btn-icon p-0 m-0 border-0 bg-transparent"
      style={{
        height: "16px",
        outline: "none",
        boxShadow: "none",
        color: hovered ? "#009ef7" : "#a1a5b7",
        cursor: "pointer",
        transition: "color 0.2s ease",
      }}
    >
      {direction === "up" ? (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 15L12 9L6 15"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 9L12 15L18 9"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
};

const TimePartSpinner = ({ value, min, max, onChange }: any) => {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const [inputValue, setInputValue] = useState(pad(value));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setInputValue(pad(value));
    }
  }, [value, isFocused]);

  const handleInputChange = (e: any) => {
    let valStr = e.target.value.replace(/\D/g, "").slice(0, 2);

    if (valStr.length > 0) {
      let num = Number(valStr);
      if (num > max) {
        num = max;
        valStr = num.toString();
      }
      if (num < min) {
        num = min;
        valStr = num.toString();
      }
      setInputValue(valStr);
      onChange(num);
    } else {
      setInputValue("");
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    setInputValue(pad(value));
  };

  const increment = () => {
    let nextVal = value + 1;
    if (nextVal > max) nextVal = min;
    onChange(nextVal);
  };

  const decrement = () => {
    let prevVal = value - 1;
    if (prevVal < min) prevVal = max;
    onChange(prevVal);
  };

  return (
    <div
      className="d-flex flex-column align-items-center bg-white border rounded px-1 py-1"
      style={{ width: "48px", borderColor: "#dbdfe9" }}
    >
      <SpinnerButton onClick={increment} direction="up" />

      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        className="form-control text-center p-0 border-0 fw-bolder fs-14 text-dark bg-transparent"
        style={{ width: "100%", height: "24px", boxShadow: "none" }}
      />

      <SpinnerButton onClick={decrement} direction="down" />
    </div>
  );
};

const CustomTimeInput = ({ date, parentDate, onCustomChange }: any) => {
  const currentDate = parentDate
    ? new Date(parentDate)
    : date
      ? new Date(date)
      : convertToEATDate(new Date());
  const currentHour = currentDate.getHours();
  const currentMinute = currentDate.getMinutes();
  const currentSecond = currentDate.getSeconds();

  const handleTimePartChange = (part: "h" | "m" | "s", val: number) => {
    const newDate = parentDate
      ? new Date(parentDate)
      : date
        ? new Date(date)
        : convertToEATDate(new Date());
    if (part === "h") newDate.setHours(val);
    if (part === "m") newDate.setMinutes(val);
    if (part === "s") newDate.setSeconds(val);
    newDate.setMilliseconds(0);

    const maxEAT = convertToEATDate(new Date());
    const clampedDate = newDate.getTime() > maxEAT.getTime() ? maxEAT : newDate;

    if (onCustomChange) {
      onCustomChange(clampedDate);
    }
  };

  return (
    <div
      className="d-flex align-items-center gap-1 m-auto justify-content-center py-1 px-2 bg-light border-r8px border"
      style={{ width: "190px" }}
    >
      <TimePartSpinner
        value={currentHour}
        min={0}
        max={24}
        onChange={(val: number) => handleTimePartChange("h", val)}
      />
      <span className="fw-bolder fs-14 text-gray-500">:</span>
      <TimePartSpinner
        value={currentMinute}
        min={0}
        max={59}
        onChange={(val: number) => handleTimePartChange("m", val)}
      />
      <span className="fw-bolder fs-14 text-gray-500">:</span>
      <TimePartSpinner
        value={currentSecond}
        min={0}
        max={59}
        onChange={(val: number) => handleTimePartChange("s", val)}
      />
    </div>
  );
};

const GRNDetails = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();

  const [loading, setLoading] = useState(false);
  const { state }: any = useLocation();
  const [initLoader, setInitLoader] = useState(true);
  const [initData, setInitData] = useState<any>([]);

  const [details, setDetails] = useState<any>(null);

  const [documentPreview, setDocumentPreview] = useState<
    Array<{
      name: string;
      url: string;
      uploadedAt: string;
      type: "image" | "pdf" | "other";
      file: File;
    }>
  >([]);
  const [documentUploadError, setDocumentUploadError] = useState<string | null>(
    null,
  );

  const [grnValidation, setGrnValidation] = useState<any>({
    invoiceNumber: false,
    driverName: false,
    driverPhone: false,
    vehicleNumber: false,
    gateEntryNumber: false,
    quantityIssue: false,
    receiptNo: false,
    receiptVerificationCode: false,
    deliveryDateTime: false,
    relevantDocument: false,
  });

  useEffect(() => {
    const fetchInitialBatchData = async (variantsList: any) => {
      const variantIds = variantsList
        ?.map((v: any) => v.variant?._id || v.variant)
        .filter(Boolean);
      console.log("selectedWarehouse", selectedWarehouse);
      if (variantIds && variantIds.length > 0) {
        const apiService = new APICallService(
          inventory.initReceivedDirectGoods,
          { variants: variantIds },
          "",
          "",
          "",
          "",
          AllModules,
          selectedWarehouse?.value || null,
        );
        const response = await apiService.callAPI();
        if (response && response.records) {
          setInitData(response.records);
        }
      }
    };

    const temp = {
      ...(state?.details || {}),
      driverName: "",
      invoiceNumber: "",
      invoiceAmount: "",
      driverPhone: "",
      vehicleNumber: "",
      receiptNo: "",
      receiptVerificationCode: "",
      deliveryDateTime: convertToEATDate(new Date()),
      reason: "",
      edDNumber: "",
      tStatus: "",
      gateEntryNumber: "",
    };
    temp.variants = temp?.variants?.map((item: any) => {
      return {
        ...item,
        batches: [],
        quantityTypes: item?.quantityTypes?.map((qItem: any) => {
          return {
            ...qItem,
            newStockCount:
              qItem?.stockCount -
              qItem?.receivedStockCount -
              qItem?.rejectedStockCount || 0,
            reason: "",
          };
        }),
      };
    });
    setDetails(temp);
    fetchInitialBatchData(temp.variants).finally(() => setInitLoader(false));
  }, []);

  const handleDocumentUpload = (event: any) => {
    const files: FileList | null = event.target.files;
    if (!files || !files.length) return;

    const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
    const selectedFiles = Array.from(files);
    const invalidFile = selectedFiles.find((file) => file.size > MAX_SIZE);
    if (invalidFile) {
      setDocumentUploadError("File size must not exceed 5 MB.");
      event.target.value = null;
      return;
    }

    const imageFiles = selectedFiles.filter((file) => file.type.startsWith("image/"));
    if (!imageFiles.length) {
      setDocumentUploadError("Please select at least one image file.");
      event.target.value = null;
      return;
    }

    setDocumentUploadError(null);
    const uploadedDocuments = imageFiles.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
      uploadedAt: new Date().toISOString(),
      type: "image" as const,
      file,
    }));

    setDocumentPreview((prev) => [...prev, ...uploadedDocuments]);
    setGrnValidation((prev: any) => ({ ...prev, relevantDocument: false }));

    event.target.value = null;
  };

  const handleRemoveDocument = (removeIndex: number) => {
    setDocumentPreview((prev) => {
      const nextDocuments = prev.filter((_, index) => index !== removeIndex);
      const removedDocument = prev[removeIndex];
      if (removedDocument?.url) {
        URL.revokeObjectURL(removedDocument.url);
      }
      if (!nextDocuments.length) {
        setGrnValidation((current: any) => ({
          ...current,
          relevantDocument: true,
        }));
      }
      return nextDocuments;
    });
  };

  if (initLoader || !details) {
    return (
      <div className="d-flex justify-content-center p-10">
        <Loader loading={initLoader} />
      </div>
    );
  }

  const getQuantityType = (quantityTypes: any, type: any) => {
    let temp = quantityTypes?.filter((item: any) => item?.type == type);
    return temp?.length ? temp : null;
  };

  const getQuantityTypeIndex = (quantityTypes: any, type: any) => {
    return quantityTypes?.findIndex((item: any) => item?.type == type);
  };

  const handleQuantityChange = (
    value: any,
    index: any,
    qIndex: any,
    name: any,
  ) => {
    const temp = { ...details };
    if (name == "stockCount") {
      temp.variants[index].quantityTypes[qIndex].newStockCount = value;
    } else if (name == "reason") {
      temp.variants[index].quantityTypes[qIndex].reason = value;
    }
    setDetails(temp);
  };

  const findObjectById = (array: any, targetId: string) => {
    return array.find((item: any) => item.variant === targetId) || null;
  };

  const onAddBatch = (variantIndex: number, quantityType: number | null) => {
    setDetails((prevDetails: any) => {
      const temp = { ...prevDetails };
      const variant = temp.variants[variantIndex];
      const variantId = variant.variant?._id || variant.variant;

      const serverInitObj = findObjectById(initData, variantId);
      const serverBaseBatch = serverInitObj?.batch || 1;

      let nextBatchNum = serverBaseBatch;
      if (variant.batches && variant.batches.length > 0) {
        const filteredBatches = variant.batches.filter(
          (b: any) => b.quantityType === quantityType,
        );
        if (filteredBatches.length > 0) {
          const maxBatch = Math.max(
            ...filteredBatches.map((b: any) => b.batchNumber),
          );
          if (maxBatch >= nextBatchNum) nextBatchNum = maxBatch + 1;
        }
      }

      variant.batches.push({
        quantityType: quantityType,
        batchNumber: nextBatchNum,
        expiryDate: null,
        stockCount: 0,
        rejectedStockCount: 0,
        reason: "",
      });

      return temp;
    });
  };

  const onRemoveBatch = (
    variantIndex: number,
    batchIndexInFiltered: number,
    quantityType: number | null,
  ) => {
    setDetails((prev: any) => {
      const temp = { ...prev };
      const batches = temp.variants[variantIndex].batches;
      let matchCount = -1;
      const actualIndex = batches.findIndex((b: any) => {
        if (b.quantityType === quantityType) matchCount++;
        return matchCount === batchIndexInFiltered;
      });

      if (actualIndex > -1) {
        batches.splice(actualIndex, 1);
      }
      return temp;
    });
  };

  const onBatchFieldChange = (
    variantIndex: number,
    qType: number | null,
    filteredIndex: number,
    field: string,
    value: any,
  ) => {
    setDetails((prev: any) => {
      const temp = { ...prev };
      const batches = temp.variants[variantIndex].batches;
      let matchCount = -1;
      const actualIndex = batches.findIndex((b: any) => {
        if (b.quantityType === qType) matchCount++;
        return matchCount === filteredIndex;
      });

      if (actualIndex > -1) {
        batches[actualIndex][field] = value;
      }
      return temp;
    });
  };
  const onBatchQuantityChange = (
    variantIndex: number,
    qType: number | null,
    filteredIndex: number,
    value: string,
    availableQty: number,
  ) => {
    if (value === "") {
      onBatchFieldChange(variantIndex, qType, filteredIndex, "stockCount", "");
      return;
    }

    const currentBatches = getQuantityBatches(variantIndex, qType);
    let otherSum = 0;
    currentBatches.forEach((b: any, bIdx: number) => {
      if (bIdx !== filteredIndex) {
        otherSum += parseInt(b.stockCount) || 0;
      }
    });

    const maxAllowed = Math.max(0, availableQty - otherSum);
    let inputVal = parseInt(value) || 0;

    if (inputVal > maxAllowed) {
      inputVal = maxAllowed;
    }

    onBatchFieldChange(
      variantIndex,
      qType,
      filteredIndex,
      "stockCount",
      inputVal,
    );
  };

  const getQuantityBatches = (index: number, quantityType: any) => {
    return (
      details?.variants[index]?.batches?.filter(
        (b: any) => b.quantityType === quantityType,
      ) || []
    );
  };

  const checkIsStockReduce = (index: number, quantityTypes: any, type: any) => {
    let temp = quantityTypes?.find((item: any) => item?.type == type);
    const quantityBatches = getQuantityBatches(index, type);
    const sum = quantityBatches.reduce(
      (acc: number, batch: any) => acc + (Number(batch.stockCount) || 0),
      0,
    );
    const pending = temp
      ? Number(temp.stockCount || 0) -
      Number(temp.receivedStockCount || 0) -
      Number(temp.rejectedStockCount || 0)
      : 0;

    if (temp && pending > 0) {
      return quantityBatches.length > 0 && sum < pending;
    }
    return false;
  };

  const handleChange = (value: any, name: any) => {
    let temp = { ...details };
    let validateTemp = { ...grnValidation };
    if (
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "")
    ) {
      validateTemp[name] = true;
    } else {
      validateTemp[name] = false;
    }
    setGrnValidation(validateTemp);
    temp[name] = value;
    setDetails(temp);
  };

  const handleSubmit = async () => {
    let temp = { ...details };
    // Filter out variants that shouldn't be processed
    console.log(temp, "temp6666666");
    if (temp?.variants) {
      temp.variants = temp.variants
        .map((variant: any) => {
          // filter quantityTypes first
          const filteredQtyTypes = variant?.quantityTypes?.filter(
            (qItem: any) =>
              (qItem?.stockCount || 0) -
              (qItem?.receivedStockCount || 0) -
              (qItem?.rejectedStockCount || 0) >
              0,
          );

          return {
            ...variant,
            quantityTypes: filteredQtyTypes,
          };
        })
        // then remove variants with no quantityTypes left
        .filter(
          (variant: any) =>
            variant?.quantityTypes && variant.quantityTypes.length > 0,
        );
    }

    let validateTemp = { ...grnValidation };

    if (temp.invoiceNumber === "") {
      validateTemp.invoiceNumber = true;
    }
    if (temp.driverName === "") {
      validateTemp.driverName = true;
    }
    if (temp.driverPhone === "") {
      validateTemp.driverPhone = true;
    }
    if (temp.vehicleNumber === "") {
      validateTemp.vehicleNumber = true;
    }
    if (temp.gateEntryNumber === "") {
      validateTemp.gateEntryNumber = true;
    }
    if (!temp.receiptNo || temp.receiptNo === "") {
      validateTemp.receiptNo = true;
    }
    if (!temp.receiptVerificationCode || temp.receiptVerificationCode === "") {
      validateTemp.receiptVerificationCode = true;
    }
    if (!temp.deliveryDateTime) {
      validateTemp.deliveryDateTime = true;
    }
    if (!documentPreview.length) {
      validateTemp.relevantDocument = true;
    }

    const allBatches = (temp?.variants || []).flatMap(
      (item: any) => item?.batches || [],
    );
    const hasReceivedQuantity = allBatches.some(
      (batch: any) => Number(batch.stockCount || 0) > 0,
    );
    const hasInvalidReceivedQuantity = allBatches.some(
      (batch: any) => Number(batch.stockCount || 0) <= 0,
    );
    const hasMissingExpiryDate = allBatches.some(
      (batch: any) => !batch.expiryDate,
    );

    if (!hasReceivedQuantity) {
      error(
        "Please enter received quantity greater than 0 for at least one batch to generate GRN.",
      );
      return;
    }

    if (hasInvalidReceivedQuantity) {
      error(
        "Please enter a received quantity greater than 0 for every added batch to generate GRN.",
      );
      return;
    }

    if (hasMissingExpiryDate) {
      error(
        "Please select an expiry date for every added batch to generate GRN.",
      );
      return;
    }

    for (const item of temp?.variants || []) {
      for (const qItem of item?.quantityTypes || []) {
        const typeBatches = (item.batches || []).filter(
          (b: any) => b.quantityType === qItem.type,
        );
        const sumReceived = typeBatches.reduce(
          (acc: number, b: any) => acc + (Number(b.stockCount) || 0),
          0,
        );
        const pendingQuantity =
          qItem.stockCount -
          qItem.receivedStockCount -
          qItem.rejectedStockCount;

        if (pendingQuantity < sumReceived) {
          validateTemp.quantityIssue = true;
        }

        if (
          typeBatches.length > 0 &&
          sumReceived < pendingQuantity &&
          (!qItem?.reason || qItem?.reason === "")
        ) {
          error("Please select the reason for quantity discrepancy!");
          return;
        }
      }
    }

    setGrnValidation(validateTemp);

    if (
      validateTemp.invoiceNumber ||
      validateTemp.driverName ||
      validateTemp.driverPhone ||
      validateTemp.vehicleNumber ||
      validateTemp.gateEntryNumber ||
      validateTemp.receiptNo ||
      validateTemp.receiptVerificationCode ||
      validateTemp.deliveryDateTime ||
      validateTemp.relevantDocument ||
      validateTemp.quantityIssue
    ) {
      if (validateTemp.relevantDocument) {
        error("Please upload the relevant documents.");
      } else {
        error("Please fill in all the mandatory fields.");
      }
      return;
    }

    const formData = new FormData();
    formData.append("driverName", details?.driverName || "");
    formData.append("driverPhone", details?.driverPhone || "");
    formData.append("vehicleNumber", details?.vehicleNumber || "");
    formData.append("invoiceNumber", details?.invoiceNumber || "");
    formData.append("gateEntryNumber", details?.gateEntryNumber || "");
    formData.append("receiptNumber", details?.receiptNo || "");
    formData.append("notes", details?.receiptNo || ""); // Backward compatibility
    formData.append(
      "receiptVerificationCode",
      details?.receiptVerificationCode || "",
    );
    formData.append(
      "receiptDate",
      details?.deliveryDateTime
        ? (() => {
          const d = new Date(details.deliveryDateTime);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const date = String(d.getDate()).padStart(2, "0");
          const hours = String(d.getHours()).padStart(2, "0");
          const minutes = String(d.getMinutes()).padStart(2, "0");
          const seconds = String(d.getSeconds()).padStart(2, "0");
          return `${year}-${month}-${date}T${hours}:${minutes}:${seconds}.000+03:00`;
        })()
        : "",
    );
    formData.append("status", "9");
    documentPreview.forEach((preview) => {
      formData.append("images", preview.file);
    });

    temp?.variants?.forEach((item: any, index: number) => {
      formData.append(
        `variants[${index}][variant]`,
        item?.variant?._id || item?.variant || "",
      );
      let qIndexCounter = 0;
      const typesHandled = new Set();

      // First pass: send batches and attach reasons to the first entry of each type
      item?.batches?.forEach((batchItem: any) => {
        if (Number(batchItem.stockCount) > 0) {
          formData.append(
            `variants[${index}][quantityTypes][${qIndexCounter}][type]`,
            batchItem.quantityType,
          );
          formData.append(
            `variants[${index}][quantityTypes][${qIndexCounter}][stockCount]`,
            batchItem.stockCount,
          );
          formData.append(
            `variants[${index}][quantityTypes][${qIndexCounter}][batch]`,
            batchItem.batchNumber,
          );

          if (batchItem.expiryDate) {
            formData.append(
              `variants[${index}][quantityTypes][${qIndexCounter}][expiry]`,
              batchItem.expiryDate.toISOString(),
            );
          }

          // Attach reason and rejectedStockCount to the first entry of this type if there's a discrepancy
          if (!typesHandled.has(batchItem.quantityType)) {
            const qItem = item.quantityTypes.find(
              (q: any) => q.type === batchItem.quantityType,
            );
            if (qItem) {
              const typeBatches = (item.batches || []).filter(
                (b: any) => b.quantityType === qItem.type,
              );
              const sumReceived = typeBatches.reduce(
                (acc: number, b: any) => acc + (Number(b.stockCount) || 0),
                0,
              );
              const target =
                qItem.stockCount -
                qItem.receivedStockCount -
                qItem.rejectedStockCount;
              if (typeBatches.length > 0 && sumReceived < target) {
                formData.append(
                  `variants[${index}][quantityTypes][${qIndexCounter}][reason]`,
                  qItem.reason,
                );
                if (qItem.reason === "1" || qItem.reason === "2") {
                  const rejected = target - sumReceived;
                  formData.append(
                    `variants[${index}][quantityTypes][${qIndexCounter}][rejectedStockCount]`,
                    String(rejected > 0 ? rejected : 0),
                  );
                }
              }
            }
            typesHandled.add(batchItem.quantityType);
          }
          qIndexCounter++;
        }
      });

      // Second pass: handle types with discrepancy but 0 total received
      item?.quantityTypes?.forEach((qItem: any) => {
        if (!typesHandled.has(qItem.type)) {
          const typeBatches = (item.batches || []).filter(
            (b: any) => b.quantityType === qItem.type,
          );
          const sumReceived = typeBatches.reduce(
            (acc: number, b: any) => acc + (Number(b.stockCount) || 0),
            0,
          );
          const target =
            qItem.stockCount -
            qItem.receivedStockCount -
            qItem.rejectedStockCount;
          if (typeBatches.length > 0 && sumReceived < target) {
            formData.append(
              `variants[${index}][quantityTypes][${qIndexCounter}][type]`,
              qItem.type,
            );
            formData.append(
              `variants[${index}][quantityTypes][${qIndexCounter}][stockCount]`,
              "0",
            );
            formData.append(
              `variants[${index}][quantityTypes][${qIndexCounter}][reason]`,
              qItem.reason,
            );
            if (qItem.reason === "1" || qItem.reason === "2") {
              const rejected = target - sumReceived;
              formData.append(
                `variants[${index}][quantityTypes][${qIndexCounter}][rejectedStockCount]`,
                String(rejected > 0 ? rejected : 0),
              );
            }
            qIndexCounter++;
          }
          typesHandled.add(qItem.type);
        }
      });
    });

    setLoading(true);

    const apiCallService = new APICallService(
      poManagementEndPoints.genarateGrn,
      formData,
      { id: details?._id },
    );
    const response = await apiCallService.callAPI();
    if (response) {
      success("GRN generated successfully");
      navigate("/UProcure/purchase-order-management/purchase-order-details", {
        state: { status: details.status ?? 1, id: details?._id },
        replace: true,
      });
    }
    setLoading(false);
  };

  return (
    <>
      <div className="p-5">
        <Row className="align-items-center g-3 mb-6">
          <Col xs>
            <div className="d-flex flex-wrap align-items-center gap-3">
              <h1 className="fs-22 fw-bolder mb-0">
                Receive Goods - ({details?.refKey || ""})
              </h1>
            </div>
          </Col>
          <Col xs="auto" className="d-flex gap-3">
            <Button
              variant="light-primary"
              size="lg"
              className="uw-btn uw-btn-lg fw-700"
              onClick={() => {
                navigate(
                  "/UProcure/purchase-order-management/purchase-order-details",
                  {
                    state: { status: details.status ?? 1, id: details?._id },
                  },
                );
              }}
            >
              View PO details
            </Button>
          </Col>
        </Row>

        {/* Receive goods products summary (matches Receive Goods mock) */}
        <Row className="mb-5">
          <Col xs={12}>
            <Card className="card-bordered">
              <Card.Body className="p-0 bg-light border-r10px">
                <div className="mb-4">
                  {details?.variants?.map((product: any, index: number) => {
                    const shouldShowRow = product?.quantityTypes?.some(
                      (item: any) =>
                        (item?.stockCount || 0) -
                        (item?.receivedStockCount || 0) -
                        (item?.rejectedStockCount || 0) >
                        0,
                    );

                    if (!shouldShowRow) return null;

                    const cartonsType = getQuantityType(
                      product?.quantityTypes,
                      CartonWithDozens,
                    )
                      ? CartonWithDozens
                      : getQuantityType(
                        product?.quantityTypes,
                        CartonWithPieces,
                      )
                        ? CartonWithPieces
                        : null;

                    const cartons = cartonsType
                      ? getQuantityType(product?.quantityTypes, cartonsType)
                      : null;

                    const dozens = getQuantityType(
                      product?.quantityTypes,
                      Dozen,
                    );
                    const pieces = getQuantityType(
                      product?.quantityTypes,
                      Piece,
                    );

                    // compute available counts for each quantity group
                    const cartonsAvailable = cartons
                      ? cartons.reduce(
                        (acc: number, it: any) =>
                          acc +
                          (Number(it?.stockCount || 0) -
                            Number(it?.receivedStockCount || 0) -
                            Number(it?.rejectedStockCount || 0)),
                        0,
                      )
                      : 0;

                    const dozensAvailable = dozens
                      ? dozens.reduce(
                        (acc: number, it: any) =>
                          acc +
                          (Number(it?.stockCount || 0) -
                            Number(it?.receivedStockCount || 0) -
                            Number(it?.rejectedStockCount || 0)),
                        0,
                      )
                      : 0;

                    const piecesAvailable = pieces
                      ? pieces.reduce(
                        (acc: number, it: any) =>
                          acc +
                          (Number(it?.stockCount || 0) -
                            Number(it?.receivedStockCount || 0) -
                            Number(it?.rejectedStockCount || 0)),
                        0,
                      )
                      : 0;

                    return (
                      <div className="mb-4" key={product?.variant?._id || product?.variant || index}>
                        {index > 0 && <hr className="my-0" />}
                        <div className="px-4 py-3">
                          <div className="d-flex align-items-center">
                            <div className="symbol symbol-50px border me-3">
                              <ImageWithFallback
                                src={
                                  product?.media?.[0]?.url ||
                                  product?.variant?.media?.[0]?.url ||
                                  ""
                                }
                                alt={
                                  product?.variantName ||
                                  product?.productName ||
                                  product?.variant?.title ||
                                  ""
                                }
                                className="object-fit-contain"
                              />
                            </div>
                            <div className="fs-15 fw-600">
                              {(
                                product?.variantName ||
                                product?.productName ||
                                product?.variant?.title ||
                                ""
                              ).replace(/\s*\)\s*/g, ")")}
                            </div>
                          </div>
                        </div>
                        <hr className="my-0" />
                        <div className="py-3 px-4">
                          <Row className="g-4 align-items-start text-center">
                            <Col md={4}>
                              <div className="fs-16 fw-600 mb-2">Cartons</div>
                              <Row className="g-4 align-items-start text-center mb-2">
                                <Col>
                                  <div className="fs-14 fw-600">
                                    Ordered / Pending Quantity
                                  </div>
                                  <div className="fs-16 fw-600 mt-1">
                                    {cartons && cartons.length
                                      ? cartons.map((it: any, i: number) => (
                                        <div key={i}>
                                          {it?.stockCount -
                                            it?.receivedStockCount -
                                            it?.rejectedStockCount || 0}
                                        </div>
                                      ))
                                      : "-"}
                                  </div>
                                </Col>
                              </Row>
                              <hr className="text-muted mt-2 mb-3" />
                              <div className="fs-14 fw-600 mb-3 text-center">
                                Received Batches
                              </div>
                              <div className="text-start">
                                {cartons &&
                                  getQuantityBatches(index, cartonsType)
                                    .length > 0 && (
                                    <div className="d-flex align-items-center mb-2 justify-content-between px-1">
                                      <div style={{ minWidth: "80px" }}></div>
                                      <div className="me-1 flex-grow-1 fs-12 fw-bolder text-center text-muted">
                                        Expires on
                                      </div>
                                      <div
                                        className="me-1 fs-12 fw-bolder text-center text-muted"
                                        style={{
                                          maxWidth: "60px",
                                          width: "60px",
                                        }}
                                      >
                                        Qty
                                      </div>
                                      <div style={{ minWidth: "16px" }}></div>
                                    </div>
                                  )}
                                {cartons &&
                                  getQuantityBatches(index, cartonsType).length >
                                  0 ? (
                                  getQuantityBatches(index, cartonsType).map(
                                    (batchObj: any, bIndex: number) => (
                                      <div
                                        key={bIndex}
                                        className="d-flex align-items-center mb-2 justify-content-between"
                                      >
                                        <div
                                          className="me-1 d-flex align-items-center"
                                          style={{ minWidth: "80px" }}
                                        >
                                          <img
                                            src={BatchIcon}
                                            width="16"
                                            className="me-1"
                                            alt="batch"
                                          />
                                          <span className="fs-12 fw-bolder">
                                            Batch {batchObj.batchNumber}
                                          </span>
                                        </div>
                                        <div className="me-1 flex-grow-1">
                                          <DatePicker
                                            selected={
                                              batchObj.expiryDate || null
                                            }
                                            onChange={(date: any) =>
                                              onBatchFieldChange(
                                                index,
                                                cartonsType,
                                                bIndex,
                                                "expiryDate",
                                                date,
                                              )
                                            }
                                            className="form-control form-control-solid fw-bolder fs-13 text-center p-2 bg-light border-r10px min-w-80px w-100"
                                            placeholderText="Expiry Date"
                                            dateFormat="dd/MM/yyyy"
                                            minDate={new Date()}
                                          />
                                        </div>
                                        <div
                                          className="me-1"
                                          style={{ maxWidth: "60px" }}
                                        >
                                          <Form.Control
                                            value={batchObj.stockCount}
                                            onChange={(e: any) =>
                                              onBatchQuantityChange(
                                                index,
                                                cartonsType,
                                                bIndex,
                                                e.target.value,
                                                cartonsAvailable,
                                              )
                                            }
                                            className="form-control form-control-solid text-center p-2 fs-13"
                                          />
                                        </div>
                                        <div
                                          style={{
                                            cursor: "pointer",
                                            minWidth: "16px",
                                          }}
                                          onClick={() =>
                                            onRemoveBatch(
                                              index,
                                              bIndex,
                                              cartonsType,
                                            )
                                          }
                                        >
                                          <img
                                            src={Remove}
                                            width="16"
                                            alt="remove"
                                          />
                                        </div>
                                      </div>
                                    ),
                                  )
                                ) : (
                                  <div className="fs-14 fw-bold text-muted border-1 form-control bg-light border-r10px d-flex align-items-center justify-content-center p-2">
                                    No batches
                                  </div>
                                )}
                                {cartons &&
                                  cartons.length > 0 &&
                                  cartonsAvailable > 0 &&
                                  Method.hasPermission(
                                    PermissionModule.GoodsReceivedNote,
                                    Permissions.Edit,
                                    currentUser,
                                  ) && (
                                    <div className="mt-2 text-start">
                                      <span
                                        className="text-primary fw-bolder fs-13 cursor-pointer"
                                        onClick={() =>
                                          onAddBatch(index, cartonsType)
                                        }
                                      >
                                        + Add new batch
                                      </span>
                                    </div>
                                  )}
                              </div>
                              <Row className="mt-3">
                                <Col>
                                  {cartonsType &&
                                    checkIsStockReduce(
                                      index,
                                      product?.quantityTypes,
                                      cartonsType,
                                    ) && (
                                      <CustomSelect
                                        minWidth="100%"
                                        backgroundColor="white"
                                        placeholder="Select reason"
                                        minHeight="45px"
                                        options={quantityDiscrepancyOptions.map(
                                          (opt) => ({
                                            label: opt.label,
                                            value: opt.value,
                                            title: opt.label,
                                          }),
                                        )}
                                        onChange={(event: any) => {
                                          handleQuantityChange(
                                            event.value,
                                            index,
                                            getQuantityTypeIndex(
                                              product?.quantityTypes,
                                              cartonsType,
                                            ),
                                            "reason",
                                          );
                                        }}
                                      />
                                    )}
                                </Col>
                              </Row>
                            </Col>

                            <Col md={4}>
                              <div className="fs-16 fw-600 mb-2">Dozens</div>
                              <Row className="g-4 align-items-start text-center mb-2">
                                <Col>
                                  <div className="fs-14 fw-600">
                                    Ordered / Pending Quantity
                                  </div>
                                  <div className="fs-16 fw-600 mt-1">
                                    {dozens && dozens.length
                                      ? dozens.map((it: any, i: number) => (
                                        <div key={i}>
                                          {it?.stockCount -
                                            it?.receivedStockCount -
                                            it?.rejectedStockCount || 0}
                                        </div>
                                      ))
                                      : "-"}
                                  </div>
                                </Col>
                              </Row>
                              <hr className="text-muted mt-2 mb-3" />
                              <div className="fs-14 fw-600 mb-3 text-center">
                                Received Batches
                              </div>
                              <div className="text-start">
                                {dozens &&
                                  getQuantityBatches(index, Dozen).length >
                                  0 && (
                                    <div className="d-flex align-items-center mb-2 justify-content-between px-1">
                                      <div style={{ minWidth: "80px" }}></div>
                                      <div className="me-1 flex-grow-1 fs-12 fw-bolder text-center text-muted">
                                        Expires on
                                      </div>
                                      <div
                                        className="me-1 fs-12 fw-bolder text-center text-muted"
                                        style={{
                                          maxWidth: "60px",
                                          width: "60px",
                                        }}
                                      >
                                        Qty
                                      </div>
                                      <div style={{ minWidth: "16px" }}></div>
                                    </div>
                                  )}
                                {dozens &&
                                  getQuantityBatches(index, Dozen).length > 0 ? (
                                  getQuantityBatches(index, Dozen).map(
                                    (batchObj: any, bIndex: number) => (
                                      <div
                                        key={bIndex}
                                        className="d-flex align-items-center mb-2 justify-content-between"
                                      >
                                        <div
                                          className="me-1 d-flex align-items-center"
                                          style={{ minWidth: "80px" }}
                                        >
                                          <img
                                            src={BatchIcon}
                                            width="16"
                                            className="me-1"
                                            alt="batch"
                                          />
                                          <span className="fs-12 fw-bolder">
                                            Batch {batchObj.batchNumber}
                                          </span>
                                        </div>
                                        <div className="me-1 flex-grow-1">
                                          <DatePicker
                                            selected={
                                              batchObj.expiryDate || null
                                            }
                                            onChange={(date: any) =>
                                              onBatchFieldChange(
                                                index,
                                                Dozen,
                                                bIndex,
                                                "expiryDate",
                                                date,
                                              )
                                            }
                                            className="form-control form-control-solid fw-bolder fs-13 text-center p-2 bg-light border-r10px min-w-80px w-100"
                                            placeholderText="Expiry Date"
                                            dateFormat="dd/MM/yyyy"
                                            minDate={new Date()}
                                          />
                                        </div>
                                        <div
                                          className="me-1"
                                          style={{ maxWidth: "60px" }}
                                        >
                                          <Form.Control
                                            value={batchObj.stockCount}
                                            onChange={(e: any) =>
                                              onBatchQuantityChange(
                                                index,
                                                Dozen,
                                                bIndex,
                                                e.target.value,
                                                dozensAvailable,
                                              )
                                            }
                                            className="form-control form-control-solid text-center p-2 fs-13"
                                          />
                                        </div>
                                        <div
                                          style={{
                                            cursor: "pointer",
                                            minWidth: "16px",
                                          }}
                                          onClick={() =>
                                            onRemoveBatch(index, bIndex, Dozen)
                                          }
                                        >
                                          <img
                                            src={Remove}
                                            width="16"
                                            alt="remove"
                                          />
                                        </div>
                                      </div>
                                    ),
                                  )
                                ) : (
                                  <div className="fs-14 fw-bold text-muted border-1 form-control bg-light border-r10px d-flex align-items-center justify-content-center p-2">
                                    No batches
                                  </div>
                                )}
                                {dozens &&
                                  dozens.length > 0 &&
                                  dozensAvailable > 0 &&
                                  Method.hasPermission(
                                    PermissionModule.GoodsReceivedNote,
                                    Permissions.Edit,
                                    currentUser,
                                  ) && (
                                    <div className="mt-2 text-start">
                                      <span
                                        className="text-primary fw-bolder fs-13 cursor-pointer"
                                        onClick={() => onAddBatch(index, Dozen)}
                                      >
                                        + Add new batch
                                      </span>
                                    </div>
                                  )}
                              </div>
                              <Row className="mt-3">
                                <Col>
                                  {dozens &&
                                    checkIsStockReduce(
                                      index,
                                      product?.quantityTypes,
                                      Dozen,
                                    ) && (
                                      <CustomSelect
                                        minWidth="100%"
                                        backgroundColor="white"
                                        placeholder="Select reason"
                                        minHeight="45px"
                                        options={quantityDiscrepancyOptions.map(
                                          (opt) => ({
                                            label: opt.label,
                                            value: opt.value,
                                            title: opt.label,
                                          }),
                                        )}
                                        onChange={(event: any) => {
                                          handleQuantityChange(
                                            event.value,
                                            index,
                                            getQuantityTypeIndex(
                                              product?.quantityTypes,
                                              Dozen,
                                            ),
                                            "reason",
                                          );
                                        }}
                                      />
                                    )}
                                </Col>
                              </Row>
                            </Col>

                            <Col md={4}>
                              <div className="fs-16 fw-600 mb-2">Pieces</div>
                              <Row className="g-4 align-items-start text-center mb-2">
                                <Col>
                                  <div className="fs-14 fw-600">
                                    Ordered / Pending Quantity
                                  </div>
                                  <div className="fs-16 fw-600 mt-1">
                                    {pieces && pieces.length
                                      ? pieces.map((it: any, i: number) => (
                                        <div key={i}>
                                          {it?.stockCount -
                                            it?.receivedStockCount -
                                            it?.rejectedStockCount || 0}
                                        </div>
                                      ))
                                      : "-"}
                                  </div>
                                </Col>
                              </Row>
                              <hr className="text-muted mt-2 mb-3" />
                              <div className="fs-14 fw-600 mb-3 text-center">
                                Received Batches
                              </div>
                              <div className="text-start">
                                {pieces &&
                                  getQuantityBatches(index, Piece).length >
                                  0 && (
                                    <div className="d-flex align-items-center mb-2 justify-content-between px-1">
                                      <div style={{ minWidth: "80px" }}></div>
                                      <div className="me-1 flex-grow-1 fs-12 fw-bolder text-center text-muted">
                                        Expires on
                                      </div>
                                      <div
                                        className="me-1 fs-12 fw-bolder text-center text-muted"
                                        style={{
                                          maxWidth: "60px",
                                          width: "60px",
                                        }}
                                      >
                                        Qty
                                      </div>
                                      <div style={{ minWidth: "16px" }}></div>
                                    </div>
                                  )}
                                {pieces &&
                                  getQuantityBatches(index, Piece).length > 0 ? (
                                  getQuantityBatches(index, Piece).map(
                                    (batchObj: any, bIndex: number) => (
                                      <div
                                        key={bIndex}
                                        className="d-flex align-items-center mb-2 justify-content-between"
                                      >
                                        <div
                                          className="me-1 d-flex align-items-center"
                                          style={{ minWidth: "80px" }}
                                        >
                                          <img
                                            src={BatchIcon}
                                            width="16"
                                            className="me-1"
                                            alt="batch"
                                          />
                                          <span className="fs-12 fw-bolder">
                                            Batch {batchObj.batchNumber}
                                          </span>
                                        </div>
                                        <div className="me-1 flex-grow-1">
                                          <DatePicker
                                            selected={
                                              batchObj.expiryDate || null
                                            }
                                            onChange={(date: any) =>
                                              onBatchFieldChange(
                                                index,
                                                Piece,
                                                bIndex,
                                                "expiryDate",
                                                date,
                                              )
                                            }
                                            className="form-control form-control-solid fw-bolder fs-13 text-center p-2 bg-light border-r10px min-w-80px w-100"
                                            placeholderText="Expiry Date"
                                            dateFormat="dd/MM/yyyy"
                                            minDate={new Date()}
                                          />
                                        </div>
                                        <div
                                          className="me-1"
                                          style={{ maxWidth: "60px" }}
                                        >
                                          <Form.Control
                                            value={batchObj.stockCount}
                                            onChange={(e: any) =>
                                              onBatchQuantityChange(
                                                index,
                                                Piece,
                                                bIndex,
                                                e.target.value,
                                                piecesAvailable,
                                              )
                                            }
                                            className="form-control form-control-solid text-center p-2 fs-13"
                                          />
                                        </div>
                                        <div
                                          style={{
                                            cursor: "pointer",
                                            minWidth: "16px",
                                          }}
                                          onClick={() =>
                                            onRemoveBatch(index, bIndex, Piece)
                                          }
                                        >
                                          <img
                                            src={Remove}
                                            width="16"
                                            alt="remove"
                                          />
                                        </div>
                                      </div>
                                    ),
                                  )
                                ) : (
                                  <div className="fs-14 fw-bold text-muted border-1 form-control bg-light border-r10px d-flex align-items-center justify-content-center p-2">
                                    No batches
                                  </div>
                                )}
                                {pieces &&
                                  pieces.length > 0 &&
                                  piecesAvailable > 0 &&
                                  Method.hasPermission(
                                    PermissionModule.GoodsReceivedNote,
                                    Permissions.Edit,
                                    currentUser,
                                  ) && (
                                    <div className="mt-2 text-start">
                                      <span
                                        className="text-primary fw-bolder fs-13 cursor-pointer"
                                        onClick={() => onAddBatch(index, Piece)}
                                      >
                                        + Add new batch
                                      </span>
                                    </div>
                                  )}
                              </div>
                              <Row className="mt-3">
                                <Col>
                                  {pieces &&
                                    checkIsStockReduce(
                                      index,
                                      product?.quantityTypes,
                                      Piece,
                                    ) && (
                                      <CustomSelect
                                        minWidth="100%"
                                        backgroundColor="white"
                                        placeholder="Select reason"
                                        minHeight="45px"
                                        options={quantityDiscrepancyOptions.map(
                                          (opt) => ({
                                            label: opt.label,
                                            value: opt.value,
                                            title: opt.label,
                                          }),
                                        )}
                                        onChange={(event: any) => {
                                          handleQuantityChange(
                                            event.value,
                                            index,
                                            getQuantityTypeIndex(
                                              product?.quantityTypes,
                                              Piece,
                                            ),
                                            "reason",
                                          );
                                        }}
                                      />
                                    )}
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    );
                  })}

                  <div className="text-center text-danger fs-20">
                    {grnValidation.quantityIssue
                      ? "Some quantity issue detected"
                      : ""}
                  </div>
                </div>

              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Delivery details section */}
        <Row className="mb-5">
          <Col xs={12}>
            <Card className=" bg-light">
              <Card.Header className="bg-light align-items-center">
                <Card.Title className="fs-20 fw-bolder mb-0">
                  Delivery details
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <Row className="gy-4">
                  <Col md={4}>
                    <Form.Label className="fs-16 fw-500 text-dark">
                      Vendor Name
                    </Form.Label>

                    <div className="d-flex align-items-center border border-r10px p-3 bg-f9f9f9">
                      <div className="symbol symbol-40px border me-3 bg-white">
                        {/* Replace with actual vendor logo when available */}
                        <ImageWithFallback
                          src={details?.business?.image || ""}
                          alt="logo"
                          className="h-100 w-100 rounded"
                        />
                      </div>
                      <div className="d-flex flex-column">
                        <span className="fs-15 fw-600 text-dark">
                          {details.business?.ownerName || ""}
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <Form.Label className="fs-16 fw-500 text-dark">
                      Delivery location
                    </Form.Label>
                    <Form.Control
                      className="form-control-custom fs-15 fw-500 text-dark bg-light ps-3"
                      placeholder="UTrade - Central Warehouse"
                      //   defaultValue="UTrade - Central Warehouse"
                      value={details?.warehouse?.address || ""}
                      disabled={true}
                    />
                  </Col>
                  <Col md={4}></Col>
                  <Col md={4}>
                    <Form.Label className="fs-16 fw-500 text-dark required">
                      Invoice number
                    </Form.Label>
                    <Form.Control
                      className={clsx(
                        "form-control-custom fs-15 fw-500 text-dark bg-white ps-3",
                        grnValidation.invoiceNumber ? "border-danger" : "",
                      )}
                      placeholder="Type here..."
                      onChange={(e) => {
                        handleChange(
                          e.target.value.trimStart(),
                          "invoiceNumber",
                        );
                      }}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="fs-16 fw-500 text-dark">
                      Delivery date
                    </Form.Label>
                    <Form.Control
                      className="form-control-custom fs-15 fw-500 text-dark bg-light ps-3"
                      placeholder="Type here..."
                      value={Method.convertDateToFormat(
                        details?.shipment?.date,
                        "DD/MM/YYYY",
                      )}
                      disabled={true}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Label className="fs-16 fw-500 text-dark required">
                      Driver name
                    </Form.Label>
                    <Form.Control
                      className={clsx(
                        "form-control-custom fs-15 fw-500 text-dark bg-white ps-3",
                        grnValidation.driverName ? "border-danger" : "",
                      )}
                      placeholder="Type here..."
                      onChange={(e) => {
                        handleChange(e.target.value.trimStart(), "driverName");
                      }}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="fs-16 fw-500 text-dark required">
                      Driver number
                    </Form.Label>
                    <Form.Control
                      className={clsx(
                        "form-control-custom fs-15 fw-500 text-dark bg-white ps-3",
                        grnValidation.driverPhone ? "border-danger" : "",
                      )}
                      placeholder="Type here..."
                      onChange={(e) => {
                        handleChange(e.target.value.trimStart(), "driverPhone");
                      }}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="fs-16 fw-500 text-dark required">
                      Vehicle number
                    </Form.Label>
                    <Form.Control
                      className={clsx(
                        "form-control-custom fs-15 fw-500 text-dark bg-white ps-3",
                        grnValidation.vehicleNumber ? "border-danger" : "",
                      )}
                      placeholder="Type here..."
                      onChange={(e) => {
                        handleChange(
                          e.target.value.trimStart(),
                          "vehicleNumber",
                        );
                      }}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Label className="fs-16 fw-500 text-dark required">
                      Gate Entry Number
                    </Form.Label>
                    <Form.Control
                      className={clsx(
                        "form-control-custom fs-15 fw-500 text-dark bg-white ps-3",
                        grnValidation.gateEntryNumber ? "border-danger" : "",
                      )}
                      placeholder="Type here..."
                      onChange={(e) => {
                        handleChange(
                          e.target.value.trimStart(),
                          "gateEntryNumber",
                        );
                      }}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Label className="fs-16 fw-500 text-dark required">
                      Receipt number
                    </Form.Label>
                    <Form.Control
                      className={clsx(
                        "form-control-custom fs-15 fw-500 text-dark bg-white ps-3",
                        grnValidation.receiptNo ? "border-danger" : "",
                      )}
                      placeholder="Type here..."
                      onChange={(e) => {
                        handleChange(e.target.value.trimStart(), "receiptNo");
                      }}
                    />
                    {grnValidation.receiptNo && (
                      <div className="text-danger fs-13 mt-1">
                        Receipt number is required.
                      </div>
                    )}
                  </Col>

                  <Col md={4}>
                    <Form.Label className="fs-16 fw-500 text-dark required">
                      Receipt verification code
                    </Form.Label>
                    <Form.Control
                      className={clsx(
                        "form-control-custom fs-15 fw-500 text-dark bg-white ps-3",
                        grnValidation.receiptVerificationCode
                          ? "border-danger"
                          : "",
                      )}
                      placeholder="Type here..."
                      onChange={(e) => {
                        handleChange(
                          e.target.value.trimStart(),
                          "receiptVerificationCode",
                        );
                      }}
                    />
                    {grnValidation.receiptVerificationCode && (
                      <div className="text-danger fs-13 mt-1">
                        Receipt verification code is required.
                      </div>
                    )}
                  </Col>

                  <Col md={4}>
                    <Form.Label className="fs-16 fw-500 text-dark required">
                      Date & time
                    </Form.Label>
                    <DatePicker
                      selected={details?.deliveryDateTime || null}
                      onChange={(date: any) => {
                        if (date) {
                          const nowEAT = convertToEATDate(new Date());
                          const base = details?.deliveryDateTime
                            ? new Date(details.deliveryDateTime)
                            : nowEAT;
                          date.setHours(base.getHours());
                          date.setMinutes(base.getMinutes());
                          date.setSeconds(base.getSeconds());
                          date.setMilliseconds(0);

                          const maxEAT = convertToEATDate(new Date());
                          if (date.getTime() > maxEAT.getTime()) {
                            date = maxEAT;
                          }
                        }
                        handleChange(date, "deliveryDateTime");
                      }}
                      maxDate={convertToEATDate(new Date())}
                      showTimeInput
                      customTimeInput={
                        <CustomTimeInput
                          parentDate={details?.deliveryDateTime}
                          onCustomChange={(date: any) =>
                            handleChange(date, "deliveryDateTime")
                          }
                        />
                      }
                      timeFormat="HH:mm:ss"
                      timeIntervals={1}
                      dateFormat="dd/MM/yyyy HH:mm:ss"
                      calendarClassName="premium-datepicker-calendar"
                      value={
                        details?.deliveryDateTime
                          ? Method.convertDateToFormat(
                            details.deliveryDateTime,
                            "DD/MM/YYYY HH:mm:ss",
                          )
                          : ""
                      }
                      className={clsx(
                        "form-control form-control-custom grn-date-picker-input fs-15 fw-500 text-dark bg-white ps-3 w-100",
                        grnValidation.deliveryDateTime ? "border-danger" : "",
                      )}
                      placeholderText="Select date & time"
                    />
                    {grnValidation.deliveryDateTime && (
                      <div className="text-danger fs-13 mt-1">
                        Receipt date is required.
                      </div>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Relevant documents & PO status */}
        <Row className="mb-4">
          <Col xs={12}>
            <Card
              className={clsx(
                "bg-light",
                grnValidation.relevantDocument ? "border border-danger" : "",
              )}
            >
              <Card.Header className="bg-light align-items-center">
                <Card.Title className="fs-20 fw-bolder mb-0 required">
                  Relevant documents <span className="text-warning">(supports multiple image upload)</span>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <Row className="g-4 align-items-stretch">
                  <Col md="auto">
                    <div className="d-flex flex-column">
                      <div className="upload-btn-wrapper">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleDocumentUpload}
                        />
                        <ImageWithFallback
                          src={UploadImg}
                          alt="upload"
                          height={200}
                          width={200}
                        />
                      </div>
                      {documentUploadError && (
                        <div className="text-danger fs-13 mt-1">
                          {documentUploadError}
                        </div>
                      )}
                    </div>
                  </Col>
                  {documentPreview.length > 0 &&
                    documentPreview.map((preview, previewIndex) => (
                      <Col md={4} key={`${preview.name}-${previewIndex}`}>
                        <div className="h-100 d-flex flex-column justify-content-between">
                          <div className="image-input image-input-outline d-flex flex-center border bg-white rounded w-lg-200px h-200px p-lg-3 p-2 mx-auto">
                            <div
                              className="image-input-wrapper shadow-none bgi-contain w-100 h-100 bgi-position-center d-flex align-items-center justify-content-center"
                              style={
                                preview.type === "image" && preview.url
                                  ? {
                                    backgroundImage: `url(${preview.url})`,
                                  }
                                  : {}
                              }
                            >
                              {preview.type !== "image" && (
                                <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100 px-3">
                                  <div className="fs-14 fw-600 text-dark text-truncate w-100 text-center">
                                    {preview.name}
                                  </div>
                                  <div className="fs-12 text-gray-600 mt-1">
                                    File
                                  </div>
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              className="uw-btn uw-btn-icon btn btn-icon btn-circle btn-light-danger position-absolute"
                              onClick={() => handleRemoveDocument(previewIndex)}
                              style={{ top: 10, right: 10, zIndex: 2 }}
                              aria-label={`Remove ${preview.name}`}
                            >
                              <ImageWithFallback
                                src={Remove}
                                alt="remove"
                                width={14}
                                height={14}
                              />
                            </button>
                          </div>
                        </div>
                      </Col>
                    ))}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {(currentUser?.userType === Admin ||
          Method.hasPermission(
            PermissionModule.POManagement,
            Permissions.PO_CREATE_GRN,
            currentUser,
          )) && (
            <div className="text-end mt-5">
              <Button
                className="uw-btn uw-btn-primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {!loading && (
                  <span className="indicator-label fs-16 fw-bold">
                    Generate GRN
                  </span>
                )}
                {loading && (
                  <span
                    className="indicator-progress fs-16 fw-bold"
                    style={{ display: "block" }}
                  >
                    Please wait...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </Button>
            </div>
          )}
      </div>
    </>
  );
};

export default GRNDetails;
